import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, memo, useContext, useRef, useState } from "react";
import uniqueId from "lodash/uniqueId";
import IntlMessageFormat from "intl-messageformat";
import { renderDefaultMenu } from "../select/Select";
import { useCombineRefs } from "../../hooks/useCombineRefs";
import { useDynamicMenuHeight } from "../../hooks/useDynamicMenuHeight";
import { useUniqueId } from "../../hooks/useUniqueId";
import { SelectMenu } from "../select/SelectMenu";
import { Popper } from "../../popper/Popper";
import { SelectOption } from "../select/SelectOption";
import { SelectLoadingOption } from "../select/SelectLoadingOption";
import { SelectMessageOption } from "../select/SelectMessageOption";
import { SelectBackOption } from "../select/SelectBackOption";
import { SelectSearchFilter } from "../select/SelectSearchFilter";
import { MenuDisplayBox } from "../select/Select.styled";
import { SelectToggle } from "../select/SelectToggle";
import { FieldsetContext } from "../fieldset/Fieldset";
import { ROUGH_FOOTER_HEIGHT, ROUGH_ITEM_HEIGHT } from "../select/constants";
import { getSelectMenuWidthStyle } from "../select/utils";
import { useStringMap } from "../../utilities/string-map/useStringMap";
import { useFocusOnMount } from "../../hooks/useFocusOnMount";
import { SelectedMultiOption } from "./SelectedMultiOption";
import { MultiSelectToggle } from "./MultiSelectToggle";
import { MultiSelectAriaContainer } from "./MultiSelectAriaContainer";
import * as Styled from "./MultiSelect.styled";
import { stringMapDefault } from "./MultiSelect.stringMap";
import { useRestoreFocusOnRemove } from "./useRestoreFocusOnRemove";
const renderDefaultSelectedMultiOption = (optionProps) => {
    const { selectedOptionProps, ...otherOptionProps } = optionProps;
    return (_jsx(SelectedMultiOption, { ...selectedOptionProps, ...otherOptionProps }, optionProps.value || uniqueId("selected-option-")));
};
const renderDefaultMenuTitle = ({ title }) => (_jsx(Styled.MenuTitleOption, { hasFooter: true, children: title }));
const renderDefaultMenuTitleCloseButton = ({ closeAndReturnFocus, label }) => (_jsx(Styled.MenuTitleCloseButton, { "aria-label": label, onClick: closeAndReturnFocus }));
const renderDefaultToggle = (toggleProps) => {
    const { toggleRef, ...otherProps } = toggleProps;
    return _jsx(MultiSelectToggle, { ref: toggleRef, ...otherProps });
};
const formatSelectedInlineOptions = (valueString, maxLabels) => {
    if (maxLabels === undefined || maxLabels <= 0 || !valueString) {
        return valueString;
    }
    const s = valueString.split(", ");
    if (s.length <= maxLabels) {
        return valueString;
    }
    return `${s.slice(0, maxLabels).join(", ")} +${s.length - maxLabels}`;
};
const renderInlineToggle = (toggleProps) => {
    const { maxLabels, selectedOption, selectedValue, toggleRef, ...otherProps } = toggleProps;
    return (_jsx(SelectToggle, { ref: toggleRef, ...otherProps, children: formatSelectedInlineOptions(selectedValue ? selectedValue : selectedOption, maxLabels) }));
};
const componentName = "MultiSelect";
const MultiSelectComponent = forwardRef((props, ref) => {
    const { applyProps, cancelProps, disabled: disabledProp, hasStatus, isFullWidth, isLoading, onBlur, onFocus, options, overlayZIndex, palette, renderToggle = renderDefaultToggle, shouldClearAncestorsOnSearch, shouldCloseMenuOnInteraction, toggleProps, values, children, defaultValues, hasCheckboxes, id, isSearchable, fieldLabel, labelId, limit, menuMaxWidth, menuMaxHeight: menuMaxHeightProp, noOptionsMessage, noSearchResultsMessage, onAdd, onChange, onMenuOpen, onMenuClose, onMenuScroll, onMenuScrollEnd, onRemove, onSearchFilter, popperProps, renderMenuTitle = renderDefaultMenuTitle, renderMenu = renderDefaultMenu, renderMenuTitleCloseButton = renderDefaultMenuTitleCloseButton, renderSearchInput, renderSelectedMultiOption = renderDefaultSelectedMultiOption, required, searchProps, stringMap: stringMapProp, renderNestedGroupLabel = stringMapProp?.nestedGroupLabel, maxLabels, onApply, onCancel, placeholder, isInline, isOptionValid, ...otherProps } = props;
    const stringMap = useStringMap(componentName, { stringMapDefault, stringMapProp });
    const fieldsetOptions = useContext(FieldsetContext);
    const idOrFallback = useUniqueId("multi-select-", id);
    const disabled = disabledProp || fieldsetOptions.disabled;
    // Refs
    const toggleWrapperRef = useRef(null);
    const menuWrapperRef = useRef(null);
    const toggleRef = useRef(null);
    const outerRef = useRef(null);
    // State
    const [selectedOptions, setSelectedOptions] = useState(values ?? defaultValues ?? []);
    const [ariaContainerValue, setAriaContainerValue] = useState();
    const { menuMaxHeight, placement, fallbackPlacements } = useDynamicMenuHeight({
        toggleRef: outerRef,
        offset: [isSearchable ? ROUGH_ITEM_HEIGHT : 0, onApply ? ROUGH_FOOTER_HEIGHT : 0].reduce((acc, next) => acc + next),
        isFullWidth
    });
    // Update functions
    const removeSelectedValue = (removedOption) => {
        const removed = typeof stringMap.removed === "function" ? (stringMap.removed(removedOption)) : (_jsx(_Fragment, { children: new IntlMessageFormat(stringMap.removed).format({
                label: typeof removedOption.label === "string" ? removedOption.label : removedOption.labelString || ""
            }) }));
        !hasCheckboxes && setAriaContainerValue(removed);
        if (values === undefined) {
            const newValues = selectedOptions.filter((obj) => obj.value !== removedOption.value);
            setSelectedOptions(newValues);
            onRemove?.(removedOption);
            onChange?.(newValues);
            return;
        }
        onRemove?.(removedOption);
        onChange?.(values.filter((obj) => obj.value !== removedOption.value));
    };
    const { added } = stringMap;
    const getAddedText = React.useMemo(() => (selectedOption) => typeof added === "function" ? (added(selectedOption)) : (_jsx(_Fragment, { children: new IntlMessageFormat(added).format({
            label: typeof selectedOption.label === "string" ? selectedOption.label : selectedOption.labelString
        }) })), [added]);
    const toggleSelectedValue = (closeAndReturnFocus) => (selectedOption, callback) => {
        const foundDuplicates = selectedOptions.filter((obj) => obj.value === selectedOption.value);
        if (values === undefined) {
            if (foundDuplicates.length === 0) {
                const newValues = selectedOptions.concat(selectedOption);
                setSelectedOptions(newValues);
                onAdd && onAdd(selectedOption);
                onChange && onChange(newValues);
                callback && callback(selectedOption, newValues);
                !hasCheckboxes && setAriaContainerValue(getAddedText(selectedOption));
                if (closeAndReturnFocus && shouldCloseMenuOnInteraction) {
                    closeAndReturnFocus();
                }
            }
            else {
                removeSelectedValue(selectedOption);
            }
        }
        else {
            const foundDuplicates = values.filter((obj) => obj.value === selectedOption.value);
            if (foundDuplicates.length === 0) {
                onAdd && onAdd([...values, selectedOption]);
                onChange && onChange([...values, selectedOption]);
                callback && callback([...values, selectedOption]);
                !hasCheckboxes && setAriaContainerValue(getAddedText(selectedOption));
                if (closeAndReturnFocus && shouldCloseMenuOnInteraction) {
                    closeAndReturnFocus();
                }
            }
            else {
                removeSelectedValue(selectedOption);
            }
        }
    };
    // TODO: update ARIA container when added or removed, disable checkboxes or otherwise visually indicate clicking won't change anything
    const hasReachedSelectedLimit = Boolean(limit && limit > 0 && (values || selectedOptions).length >= limit);
    const getTrigger = ({ isOpen, setIsOpen, ...triggerProps }) => isInline
        ? renderInlineToggle({
            ...triggerProps,
            "aria-expanded": isOpen,
            isFullWidth,
            isOpen,
            onBlur,
            onFocus,
            onKeyDown: handleKeyDown(setIsOpen),
            placeholder,
            hasStatus,
            disabled,
            options,
            palette,
            id: idOrFallback,
            labelId,
            maxLabels,
            selectedValue: values?.map((v) => (typeof v === "string" ? v : v.labelString || v.label)).join(", "),
            selectedOption: values
                ? undefined
                : selectedOptions?.map((v) => (typeof v === "string" ? v : v.labelString || v.label)).join(", "),
            toggleRef
        })
        : renderToggle({
            ...triggerProps,
            ...toggleProps,
            toggleRef,
            "aria-expanded": isOpen,
            disabled: disabled || hasReachedSelectedLimit,
            hasStatus,
            id: idOrFallback,
            isFullWidth,
            isLoading,
            isOpen,
            labelId,
            onBlur,
            onFocus,
            options,
            palette,
            required,
            selectedOptions: values || selectedOptions,
            stringMap
        });
    const getMenuContent = ({ motionState: _motionState, closeAndReturnFocus, overlayRef }) => (_jsxs(MenuDisplayBox, { ref: menuWrapperRef, depth: "menu", borderRadius: "extraLarge", hoverDepth: "menu", style: getSelectMenuWidthStyle({
            isFullWidth,
            parentWidth: toggleWrapperRef.current ? toggleWrapperRef.current.getBoundingClientRect().width : undefined,
            menuMaxWidth
        }), children: [hasCheckboxes &&
                renderMenuTitle({
                    title: fieldLabel,
                    closeAndReturnFocus
                }), renderMenu({
                "aria-labelledby": labelId ?? idOrFallback,
                applyProps,
                cancelProps,
                children,
                closeAndReturnFocus,
                isFullWidth,
                isLoading,
                isMulti: true,
                isSearchable,
                hasReachedSelectedLimit,
                keyboardDirection: "vertical",
                maxHeight: menuMaxHeightProp || menuMaxHeight,
                menuWidth: toggleWrapperRef && toggleWrapperRef.current
                    ? toggleWrapperRef.current.getBoundingClientRect().width
                    : undefined,
                noOptionsMessage,
                noSearchResultsMessage,
                onMenuOpen,
                onMenuClose,
                onMenuScroll,
                onMenuScrollEnd,
                onSearchFilter,
                onUpdate: toggleSelectedValue(closeAndReturnFocus),
                options,
                // this is marked as optional but Popper actually provides it at all times
                overlayRef,
                renderNestedGroupLabel,
                renderSearchInput,
                searchProps,
                selectedOptions: values || selectedOptions,
                shouldClearAncestorsOnSearch,
                stringMap,
                toggleRef,
                hasCheckboxes,
                onApply,
                onCancel
            }), hasCheckboxes && renderMenuTitleCloseButton({ label: stringMap.close, closeAndReturnFocus })] }));
    const hasValues = values ? values.length > 0 : selectedOptions.length > 0;
    const handleKeyDown = (setIsOpen) => (event) => {
        const preferredKeys = ["ArrowDown", "ArrowUp"];
        if (preferredKeys.includes(event.key)) {
            event.preventDefault();
            // Timeout works around bug on IE11 where opening the dropdown causes the page to scroll down.
            setTimeout(() => {
                setIsOpen(true);
            }, 0);
        }
    };
    // use useFocusOnMount hook to restore focus after pressing a remove button on a selected value (renderSelectedMultiOption)
    const { shouldFocusOnMount, handleFocus, handleBlur, handleMaybeNavigate } = useFocusOnMount({
        initialValue: false,
        containerRef: toggleWrapperRef
    });
    const lastFocusedIndex = useRef(-1);
    const mapSelectedValues = (values) => values.map((opt, index) => (_jsx(React.Fragment, { children: renderSelectedMultiOption({
            ...opt,
            hasDisabledParent: disabled,
            stringMap,
            isOptionValid: isOptionValid ? isOptionValid(opt) : true,
            removeSelectedValue: () => {
                removeSelectedValue(opt);
                lastFocusedIndex.current = index;
                handleMaybeNavigate(true);
            }
        }) }, index)));
    const { handlePopperOpen, handlePopperClose } = useRestoreFocusOnRemove({
        hasValues,
        isInline,
        lastFocusedIndex,
        popperProps,
        shouldFocusOnMount,
        toggleRef,
        toggleWrapperRef
    });
    return (_jsxs(Styled.Wrapper, { ref: useCombineRefs(ref, outerRef), "data-testid": "MultiSelect", isFullWidth: isFullWidth, ...otherProps, isInline: isInline, children: [_jsxs(Styled.ToggleOptionWrapper, { isFullWidth: isFullWidth, ref: toggleWrapperRef, "data-testid": "ToggleOptionWrapper", 
                // TODO: might need to switch between Field and Fieldset here instead
                role: hasValues && !isInline ? "list" : undefined, isInline: isInline, hasValues: hasValues, onBlur: handleBlur, onFocus: handleFocus, children: [hasValues && !isInline && _jsx(_Fragment, { children: mapSelectedValues(values ? values : selectedOptions) }), _jsx(Popper, { mode: "popover", overlay: getMenuContent, placement: placement, placementRef: toggleWrapperRef, fallbackPlacements: fallbackPlacements, ...popperProps, onOpen: handlePopperOpen, onClose: handlePopperClose, popoverOptions: {
                            mode: hasCheckboxes ? "dialog" : "auto",
                            labelId: hasCheckboxes ? labelId : undefined
                        }, trigger: getTrigger, zIndex: overlayZIndex })] }), !hasCheckboxes && _jsx(MultiSelectAriaContainer, { ariaContainerValue: ariaContainerValue })] }));
});
MultiSelectComponent.displayName = "MultiSelect";
MultiSelectComponent.defaultProps = {
    noOptionsMessage: "No Options Available",
    noSearchResultsMessage: "No Results Found",
    palette: "interactiveConstructive",
    popperProps: {},
    renderMenu: renderDefaultMenu,
    renderToggle: renderDefaultToggle,
    renderSelectedMultiOption: renderDefaultSelectedMultiOption,
    shouldCloseMenuOnInteraction: true,
    hasCheckboxes: false,
    isInline: false
};
export const MultiSelect = Object.assign(memo(MultiSelectComponent), {
    Toggle: MultiSelectToggle,
    Menu: SelectMenu,
    Option: SelectOption,
    MessageOption: SelectMessageOption,
    LoadingOption: SelectLoadingOption,
    BackOption: SelectBackOption,
    SearchFilter: SelectSearchFilter,
    SelectedMultiOption
});
MultiSelect.displayName = "MultiSelect";
